import { FC, KeyboardEvent, MouseEvent } from 'react';
import { defineMessages, useIntl } from 'react-intl';
import { Button, Pill } from 'src/components';
import { TemplateName } from 'src/api/learningSessions/types';
import { LearnableValue } from 'src/api/learningSessions/schema';
import { isLiteralTranslationLabel, isRomanizationLabel } from 'src/api/settings/learningToggles';
import { romanizationTextAttrs } from 'src/utils/pinyinFont';
import type { NextActionState } from 'src/apps/LearningSession/Common/NextActionButton';
import type { TestCardData } from 'src/apps/LearningSession/Common/learnableCards/utils';
import {
  getInstructionMsg,
  getPostAnswerDisplayedInfo,
} from 'src/apps/LearningSession/Common/learnableCards/TestLearnableCard';
import ResponseDisplay, {
  TestResponse,
} from 'src/apps/LearningSession/Common/learnableCards/TestLearnableCard/Response';
import {
  LearningSessionAction,
  ResponseState,
} from 'src/apps/LearningSession/hooks/learningSessionReducer';
import { ContinueControl } from '../ContinueControl';
import { AnimatedFlower } from '../LearnableWithFlower/AnimatedFlower';
import * as S from './styles';

const msgs = defineMessages({
  videoQuiz: { id: 'blob_session_video_quiz', defaultMessage: 'Video quiz' },
  audioQuiz: { id: 'blob_session_audio_quiz', defaultMessage: 'Audio quiz' },
  pickTheCorrectAnswer: { id: 'text_mc_test_instruction' },
  iDontKnow: { id: 'learning_session_next_button_i_dont_know' },
  checkAnswer: { id: 'learning_session_next_button_check_answer' },
});

const FEEDBACK_STATES: ResponseState[] = ['Correct', 'Incorrect', 'NearlyCorrect'];

type TestContentProps = {
  cardData: TestCardData;
  response: TestResponse;
  responseState: ResponseState;
  userAnswer: string;
  learningSessionDispatch: (action: LearningSessionAction) => void;
  definition?: LearnableValue;
  item?: LearnableValue;
  growthLevel: number;
  learnableId?: number;
  showLiteralTranslation: boolean;
  showRomanization: boolean;
  nextActionState: NextActionState;
  onNextClick: (event?: MouseEvent) => void;
  onNextKeyUp: (event: KeyboardEvent) => void;
  /** White text over a full-screen video, continuing with a swipe */
  overlay?: boolean;
};

export const TestContent: FC<TestContentProps> = ({
  cardData,
  response,
  responseState,
  userAnswer,
  learningSessionDispatch,
  definition,
  item,
  growthLevel,
  learnableId,
  showLiteralTranslation,
  showRomanization,
  nextActionState,
  onNextClick,
  onNextKeyUp,
  overlay = false,
}) => {
  const intl = useIntl();
  const literalTranslationLabel = intl.formatMessage({ id: 'literal_translation' });
  const { prompt, attributeValues, postAnswerInfo } = cardData;
  const instruction = getInstructionMsg(prompt.promptType, response.templateName);
  // Tracing tests have no instruction, so only answer-picking tests fall back to a generic one
  const isTracing = response.templateName === TemplateName.TRACING;
  const instructionText =
    instruction || !isTracing ? intl.formatMessage(instruction ?? msgs.pickTheCorrectAnswer) : null;
  const showFeedback = FEEDBACK_STATES.includes(responseState);

  const visiblePostAnswerInfo =
    postAnswerInfo &&
    (showLiteralTranslation ||
      !isLiteralTranslationLabel(postAnswerInfo.label, literalTranslationLabel)) &&
    (showRomanization || !isRomanizationLabel(postAnswerInfo.label))
      ? postAnswerInfo
      : undefined;
  // Always resolve post-answer copy so its slot can keep the same height before
  // and after the learner answers (visibility toggles; layout does not jump).
  const postAnswerDisplayedInfo = getPostAnswerDisplayedInfo({
    prompt,
    response,
    item,
    definition,
    postAnswerInfo: visiblePostAnswerInfo,
  });

  const isMultipleChoice =
    response.templateName === TemplateName.MULTIPLE_CHOICE ||
    response.templateName === TemplateName.REVERSED_MULTIPLE_CHOICE;

  const flower = (
    <S.HeaderFlower>
      <AnimatedFlower growthLevel={growthLevel} learnableId={learnableId} />
    </S.HeaderFlower>
  );

  const renderHeading = () => {
    switch (prompt.promptType) {
      case 'Video':
        return (
          <>
            {flower}
            <S.Eyebrow>{intl.formatMessage(msgs.videoQuiz)}</S.Eyebrow>
            {instructionText && <S.Heading>{instructionText}</S.Heading>}
          </>
        );
      case 'Audio':
        return (
          <>
            {flower}
            <S.Eyebrow>{intl.formatMessage(msgs.audioQuiz)}</S.Eyebrow>
            {instructionText && <S.Heading>{instructionText}</S.Heading>}
          </>
        );
      case 'Image':
        return (
          <>
            {flower}
            {instructionText && <S.Eyebrow>{instructionText}</S.Eyebrow>}
            <S.PromptImage src={prompt.image} alt="" data-testid="blob-prompt-image" />
          </>
        );
      case 'Text':
        return (
          <>
            {flower}
            {instructionText && <S.Eyebrow>{instructionText}</S.Eyebrow>}
            <S.Heading
              dir="auto"
              $showBigger={prompt.showBigger}
              data-testid="blob-prompt-text"
              {...(prompt.label && isRomanizationLabel(prompt.label)
                ? romanizationTextAttrs(prompt.text)
                : {})}
            >
              {prompt.text}
            </S.Heading>
          </>
        );
    }
  };

  const renderNextAction = () => {
    if (nextActionState === 'Unanswered') {
      return (
        <Button
          buttonType="secondary"
          buttonSize="medium"
          icon="question"
          onClick={onNextClick}
          onKeyUp={onNextKeyUp}
          data-testid="blob-i-dont-know"
        >
          {intl.formatMessage(msgs.iDontKnow)}
        </Button>
      );
    }
    if (nextActionState === 'CheckAnswer' || nextActionState === 'Selected') {
      return (
        <Button
          buttonType="primary"
          buttonSize="medium"
          onClick={onNextClick}
          onKeyUp={onNextKeyUp}
          data-testid="blob-check-answer"
        >
          {intl.formatMessage(msgs.checkAnswer)}
        </Button>
      );
    }
    return (
      <ContinueControl
        overlay={overlay}
        onClick={onNextClick}
        onKeyUp={onNextKeyUp}
        disabled={nextActionState === 'Processing'}
      />
    );
  };

  return (
    <S.Content $overlay={overlay} data-testid="blob-test-content">
      <S.Header>
        {renderHeading()}
        {attributeValues.length > 0 && (
          <S.Attributes>
            {attributeValues.map((attributeValue, index) => (
              // eslint-disable-next-line react/no-array-index-key
              <Pill key={`${attributeValue}-${index}`} color="green">
                {attributeValue}
              </Pill>
            ))}
          </S.Attributes>
        )}
      </S.Header>
      <S.Response
        $isMultipleChoice={isMultipleChoice}
        $showBiggerChoices={'showBigger' in response && response.showBigger}
        $overlay={overlay}
      >
        <ResponseDisplay
          response={response}
          largeScreenLayout={isMultipleChoice ? 'grid' : 'column'}
          responseState={responseState}
          userAnswer={userAnswer}
          learningSessionDispatch={learningSessionDispatch}
          onVideo={overlay}
        />
      </S.Response>
      {postAnswerDisplayedInfo && (
        <S.PostAnswerInfo
          data-testid="blob-post-answer-info"
          $visible={showFeedback}
          aria-hidden={!showFeedback}
        >
          <S.PostAnswerLabel>{postAnswerDisplayedInfo.label}</S.PostAnswerLabel>
          <span
            dir="auto"
            {...(isRomanizationLabel(postAnswerDisplayedInfo.label)
              ? romanizationTextAttrs(postAnswerDisplayedInfo.value as string)
              : {})}
          >
            {postAnswerDisplayedInfo.value as string}
          </span>
        </S.PostAnswerInfo>
      )}
      <S.NextAction $overlay={overlay}>{renderNextAction()}</S.NextAction>
    </S.Content>
  );
};
